// src/modules/app-views/invoices/components/InvoicesFilterPopover.jsx

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback } from 'react';
import { Button, DatePicker, Select } from 'antd';
import dayjs from 'dayjs';

import { useListContext } from 'components/core/ListProvider';
import translateX from 'utils/translateX';
import fiscalService from 'services/fiscalService';
import { getDateFormatByLocale } from 'utils/helpers';

import './InvoicesFilterPopover.less';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const DOCUMENT_TYPE_OPTIONS = [
  { value: 'nfe', labelKey: 'nfe' },
  { value: 'nfce', labelKey: 'nfce' },
];

const STATUS_OPTIONS = [
  { value: fiscalService.DOCUMENT_STATUS.DRAFT, labelKey: 'draft' },
  { value: fiscalService.DOCUMENT_STATUS.AUTHORIZED, labelKey: 'authorized' },
  { value: fiscalService.DOCUMENT_STATUS.CANCELED, labelKey: 'canceled' },
  { value: fiscalService.DOCUMENT_STATUS.CORRECTED, labelKey: 'corrected' },
  { value: fiscalService.DOCUMENT_STATUS.REJECTED, labelKey: 'rejected' },
  { value: fiscalService.DOCUMENT_STATUS.DENIED, labelKey: 'denied' },
  { value: fiscalService.DOCUMENT_STATUS.ERROR, labelKey: 'error' },
  { value: fiscalService.DOCUMENT_STATUS.VOIDED, labelKey: 'voided' },
];

// *******************************************************************************
// COMPONENT
// *******************************************************************************
const InvoicesFilterPopover = ({ onRequestClose, onOpenDrawer }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { setFilter, setDrawerFilter, clearFilters, query } = useListContext() || {};
  const format = getDateFormatByLocale();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. DERIVED VALUES — read directly from context (no local state)
  // ─────────────────────────────────────────────────────────────────────────────
  const documentTypeValue = (() => {
    const v = query?.filter?.documentType;
    return Array.isArray(v) ? v[0] : (v ?? undefined);
  })();

  const statusValue = (() => {
    const v = query?.filter?.status;
    return Array.isArray(v) ? v[0] : (v ?? undefined);
  })();

  const dateRangeValue = (() => {
    const range = query?.drawer?.dateRange;
    if (range?.length === 2 && range[0] && range[1]) return [dayjs(range[0]), dayjs(range[1])];
    return null;
  })();

  // Date presets for RangePicker
  const datePresets = [
    { label: translateX('today'), value: [dayjs(), dayjs()] },
    {
      label: translateX('this_week'),
      value: [dayjs().startOf('week'), dayjs().endOf('week')],
    },
    {
      label: translateX('this_month'),
      value: [dayjs().startOf('month'), dayjs().endOf('month')],
    },
    {
      label: translateX('last_month'),
      value: [
        dayjs().subtract(1, 'month').startOf('month'),
        dayjs().subtract(1, 'month').endOf('month'),
      ],
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS — apply immediately on change (no deferred apply)
  // ─────────────────────────────────────────────────────────────────────────────
  const handleDocumentTypeChange = useCallback(
    value => {
      setFilter?.('documentType', value != null ? [value] : undefined);
    },
    [setFilter],
  );

  const handleStatusChange = useCallback(
    value => {
      setFilter?.('status', value != null ? [value] : undefined);
    },
    [setFilter],
  );

  const handleDateRangeChange = useCallback(
    dates => {
      if (dates?.[0] && dates?.[1]) {
        setDrawerFilter?.({
          dateRange: [
            dates[0].startOf('day').toISOString(),
            dates[1].endOf('day').toISOString(),
          ],
        });
      } else {
        setDrawerFilter?.({ dateRange: undefined });
      }
    },
    [setDrawerFilter],
  );

  const handleClear = useCallback(() => {
    clearFilters?.();
    onRequestClose?.();
  }, [clearFilters, onRequestClose]);

  const handleAdvancedFilters = useCallback(() => {
    onRequestClose?.();
    onOpenDrawer?.();
  }, [onRequestClose, onOpenDrawer]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="invoices-filter-popover">
      <div className="invoices-filter-popover__title">{translateX('quick_filters')}</div>

      <div className="invoices-filter-popover__row">
        <div className="invoices-filter-popover__field">
          <div className="invoices-filter-popover__label">{translateX('type')}</div>
          <Select
            allowClear
            size="middle"
            value={documentTypeValue}
            onChange={handleDocumentTypeChange}
            options={DOCUMENT_TYPE_OPTIONS.map(o => ({
              value: o.value,
              label: translateX(o.labelKey),
            }))}
            placeholder={translateX('type')}
          />
        </div>

        <div className="invoices-filter-popover__field">
          <div className="invoices-filter-popover__label">{translateX('status')}</div>
          <Select
            allowClear
            size="middle"
            value={statusValue}
            onChange={handleStatusChange}
            options={STATUS_OPTIONS.map(o => ({
              value: o.value,
              label: translateX(o.labelKey),
            }))}
            placeholder={translateX('status')}
          />
        </div>

        <div className="invoices-filter-popover__field">
          <div className="invoices-filter-popover__label">{translateX('period')}</div>
          <DatePicker.RangePicker
            size="middle"
            format={format}
            value={dateRangeValue}
            onChange={handleDateRangeChange}
            presets={datePresets}
          />
        </div>
      </div>

      <div className="invoices-filter-popover__divider">
        <Button
          type="link"
          size="small"
          className="invoices-filter-popover__advanced-btn"
          onClick={handleAdvancedFilters}
        >
          {`${translateX('advanced_filters')} →`}
        </Button>
        <div className="invoices-filter-popover__actions">
          <Button size="small" onClick={handleClear}>
            {translateX('clear')}
          </Button>
        </div>
      </div>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default InvoicesFilterPopover;
